"use client";

import {
  useGetArtworkById,
  useGetRelatedFieldsWithAttachments,
} from "@/api/artwork/artwork";
import {
  shouldIncludeAttachment,
  useGetAttachmentsByDocId,
} from "@/api/attachment";
import { MultilingualSelect } from "@/components/form-items/MultilingualField";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/auth/AuthContext";
import { useArtworkPageData } from "@/hooks/useArtworkPageData";
import { SupportedLocale } from "@/i18n";
import { PHOTOGRAPHY_TYPE_VALUES } from "@/lib/compute-field/artwork-module-helpers";
import { PublicationStatus } from "@/lib/types";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ArtworkHeader } from "./ArtworkHeader";
import { ArtworkImages } from "./ArtworkImages";
import { ArtworkOverview } from "./ArtworkOverview";
import { ArtworkPhotoMasterInfo } from "./ArtworkPhotoMasterInfo";
import { ArtworkTirageInfo } from "./ArtworkTirageInfo";
import { ArtworkAccordions } from "./accordions";

export function ArtworkPage() {
  const { i18n, t } = useTranslation();
  const { instanceData } = useAuth();
  const [localLocale, setLocalLocale] = useState<SupportedLocale>(
    i18n.language as SupportedLocale,
  );
  const [translationsLoaded, setTranslationsLoaded] = useState(0);
  const [isLoadingTranslations, setIsLoadingTranslations] = useState(false);
  const navigate = useNavigate();

  const { id } = useParams();

  const { data: artwork } = useGetArtworkById(id);
  const { data: relatedFields } = useGetRelatedFieldsWithAttachments(id);
  const { data: unfiltetredAttachments } = useGetAttachmentsByDocId(id);

  // Recharger les traductions depuis l'API quand la langue locale change
  useEffect(() => {
    // Ne pas afficher le loader au premier chargement
    const isInitialLoad = translationsLoaded === 0;

    if (!isInitialLoad) {
      setIsLoadingTranslations(true);
    }

    // Recharger les ressources i18n pour la nouvelle langue depuis l'API
    i18n
      .reloadResources(localLocale, "translation")
      .then(() => {
        // Forcer le re-render en mettant à jour l'état après le chargement
        setTranslationsLoaded((prev) => prev + 1);
        setIsLoadingTranslations(false);
      })
      .catch((error) => {
        console.warn("Failed to reload translations:", error);
        setIsLoadingTranslations(false);
      });
    // translationsLoaded est utilisé uniquement pour détecter le premier chargement, ne pas l'inclure dans les dépendances
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localLocale, i18n]);

  // Fonction pour obtenir une traduction dans une langue spécifique sans modifier la langue de l'interface
  const getTranslation = useMemo(() => {
    const loadResourceBundle = () => {
      // Essayer d'abord depuis i18n (qui devrait avoir les ressources après reloadResources)
      let resourceBundle = i18n.getResourceBundle(localLocale, "translation");
      if (resourceBundle) return resourceBundle;

      // Si pas trouvé dans i18n, charger depuis localStorage
      try {
        // i18next-localstorage-backend utilise le format: i18next_res_{lang}-{namespace}
        const possibleKeys = [
          `i18next_res_${localLocale}-translation`, // Format avec tiret (le bon format)
        ];

        for (const storageKey of possibleKeys) {
          const stored = localStorage.getItem(storageKey);
          if (stored) {
            try {
              const parsed = JSON.parse(stored);

              // Le format peut être { translation: {...} } ou directement {...}
              if (parsed) {
                resourceBundle = parsed.translation || parsed;
                if (resourceBundle && typeof resourceBundle === "object") {
                  // Ajouter les ressources à i18n pour les prochaines fois
                  i18n.addResourceBundle(
                    localLocale,
                    "translation",
                    resourceBundle,
                    true,
                    true,
                  );
                  return resourceBundle;
                }
              }
            } catch (_parseError) {
              // Continuer avec la clé suivante
              continue;
            }
          }
        }
      } catch (e) {
        console.warn("Could not load translations from localStorage:", e);
      }

      return resourceBundle;
    };

    return (key: string, options?: any): string => {
      const resourceBundle = loadResourceBundle();

      // Si on a trouvé la ressource, utiliser la traduction
      if (resourceBundle && resourceBundle[key]) {
        let translation = resourceBundle[key];

        // Gérer l'interpolation si des options sont fournies
        if (options && typeof translation === "string") {
          Object.keys(options).forEach((optKey) => {
            if (optKey !== "lng" && optKey !== "count") {
              translation = translation.replace(
                new RegExp(`{{${optKey}}}`, "g"),
                String(options[optKey]),
              );
            }
          });
        }

        return typeof translation === "string" ? translation : key;
      }

      // Fallback: utiliser la fonction de traduction fixée
      try {
        const fixedT = i18n.getFixedT(localLocale, "translation");
        const result = fixedT(key, options);
        return typeof result === "string" ? result : key;
      } catch {
        // Dernier fallback: retourner la clé
        return key;
      }
    };
    // translationsLoaded est nécessaire pour forcer le recalcul quand les traductions sont rechargées
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n, localLocale, translationsLoaded]);

  const attachments =
    artwork &&
    unfiltetredAttachments?.filter((a) =>
      shouldIncludeAttachment(
        a,
        artwork.oeuvresPublicationStatus as PublicationStatus,
      ),
    );

  // Determine item type and prepare tirageData for draft photography artworks with published tirages
  const { itemType, artworkWithTirageData } = useMemo(() => {
    if (!artwork)
      return { itemType: "ARTWORK" as const, artworkWithTirageData: artwork };

    // If artwork has tirageData, it's a TIRAGE
    if ((artwork as any).tirageData) {
      return { itemType: "TIRAGE" as const, artworkWithTirageData: artwork };
    }

    // Check if it's a photo master by checking if type contains photography values
    const typeMultilingual = artwork.oeuvresDescriptionTypeOeuvreMultilingual;
    const isPhotography = typeMultilingual
      ? Object.values(typeMultilingual).some((value) =>
          PHOTOGRAPHY_TYPE_VALUES.includes(
            value as (typeof PHOTOGRAPHY_TYPE_VALUES)[number],
          ),
        )
      : false;

    // R3: Draft Artwork with type = "Photographie" + published Tirage -> TIRAGE(s)
    // If artwork is draft + photography type + has published tirages, select first tirage and use it
    if (isPhotography) {
      const isDraft = artwork.oeuvresPublicationStatus === "draft";
      const publishedTirages =
        (artwork as any).Tirage && Array.isArray((artwork as any).Tirage)
          ? (artwork as any).Tirage.filter(
              (t: any) => t.tiragePublicationStatus === "published",
            )
          : [];

      // If draft with published tirages, select first tirage and treat as TIRAGE
      if (isDraft && publishedTirages.length > 0) {
        const firstTirage = publishedTirages[0];
        return {
          itemType: "TIRAGE" as const,
          artworkWithTirageData: {
            ...artwork,
            tirageData: firstTirage,
          } as any,
        };
      }

      // If published and photography type, it's a PHOTO_MASTER
      if (!isDraft) {
        return {
          itemType: "PHOTO_MASTER" as const,
          artworkWithTirageData: artwork,
        };
      }
    }

    return { itemType: "ARTWORK" as const, artworkWithTirageData: artwork };
  }, [artwork]);

  // Data preparation using hook - must be called before conditional return
  const {
    displayData,
    mainImage,
    otherImages,
    isTirage,
    isPhotoMaster,
    tirageData,
    computedFields,
    isPhotoModuleEnabledValue,
  } = useArtworkPageData({
    data: artworkWithTirageData || ({} as any),
    attachments: attachments || [],
    instanceData: instanceData ?? null,
    locale: localLocale,
    t: getTranslation,
    filterFromGeneralCR: { showPublicAuctions: true }, // TODO: Get from settings if available
    itemType,
  });

  // Relation check - must be called before conditional return
  const relationNotEmpty = useMemo(() => {
    if (!relatedFields) return false;
    return (
      (relatedFields.relatedArtwork?.length ?? 0) > 0 ||
      (relatedFields.from_Artwork_relatedArtwork?.length ?? 0) > 0 ||
      (relatedFields.relatedAudiovisual?.length ?? 0) > 0 ||
      (relatedFields.relatedDocument?.length ?? 0) > 0 ||
      (relatedFields.relatedMultipleprint?.length ?? 0) > 0 ||
      (relatedFields.relatedPhotography?.length ?? 0) > 0 ||
      (relatedFields.relatedWriting?.length ?? 0) > 0
    );
  }, [relatedFields]);

  // Early return after all hooks have been called
  if (!artwork) {
    return <div>{t("artworkNexistePasLabel")}</div>;
  }

  return (
    <div className="w-full min-h-screen">
      <div className="flex items-center justify-between w-full h-[70px] bg-[#FCFCFD] border-y px-5 fixed top-0 left-0 right-0 z-40">
        <p></p>
        <p className="font-semibold text-lg xs:hidden md:block">
          {displayData?.oeuvresDescriptionTitre}
        </p>
        <div className="flex items-center gap-3">
          <MultilingualSelect
            value={localLocale}
            onValueChange={(value) => setLocalLocale(value as SupportedLocale)}
          />
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-sm font-semibold text-gray-950 cursor-pointer border"
          >
            {t("Close")}
          </Button>
        </div>
      </div>
      <div className="w-full relative pt-[70px]">
        {isLoadingTranslations && (
          <div className="fixed inset-0 top-[70px] bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
          </div>
        )}
        <div className="w-full max-w-screen-2xl m-auto md:p-10 xs:p-5">
          <div className="flex xs:flex-col md:flex-row items-start justify-center text-center gap-10">
            <ArtworkImages
              mainImage={mainImage}
              otherImages={otherImages}
              locale={localLocale}
            />
            <div className="w-full h-full flex flex-col gap-5 text-left md:pr-16">
              <div className="flex items-center justify-between">
                <p className="font-normal text-base subpixel-antialiased">
                  {computedFields.artistsCalculated}
                </p>
              </div>
              <ArtworkHeader
                title={displayData?.oeuvresDescriptionTitre || ""}
                registrationNumber={
                  displayData?.oeuvresDescriptionNumeroEnregistrement || ""
                }
                t={getTranslation}
              />
              <ArtworkOverview
                displayData={displayData}
                locale={localLocale}
                itemType={
                  isTirage
                    ? "TIRAGE"
                    : isPhotoMaster
                      ? "PHOTO_MASTER"
                      : "ARTWORK"
                }
                t={getTranslation}
                overviewSummaryCalculated={
                  computedFields.overviewSummaryCalculated
                }
              />
              {isTirage && tirageData && (
                <ArtworkTirageInfo
                  tirageData={tirageData}
                  locale={localLocale}
                  t={getTranslation}
                />
              )}
              {isPhotoMaster && (
                <ArtworkPhotoMasterInfo
                  data={artwork}
                  displayData={displayData}
                  locale={localLocale}
                  t={getTranslation}
                />
              )}
              <ArtworkAccordions
                displayData={displayData}
                data={artwork}
                computedFields={computedFields}
                relatedFieldsWithAttachments={relatedFields || {}}
                relationNotEmpty={relationNotEmpty}
                isTirage={isTirage}
                isPhotoMaster={isPhotoMaster}
                isPhotoModuleEnabled={isPhotoModuleEnabledValue}
                locale={localLocale}
                t={getTranslation}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
